import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  CancelIcon,
  Delete01Icon,
  MailSend01Icon,
  PlusSignIcon,
  UserGroupIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import {
  Header,
  HeaderButton,
  HeaderCopyButton,
  HeaderOrganizationMenu,
} from '@/ui/components/header';
import { Breadcrumb, BreadcrumbPage } from '@/ui/components/breadcrumb';
import {
  MainActions,
  MainActionsEnd,
  MainActionsSearchInput,
  MainHeader,
  MainScrollArea,
} from '@/ui/components/main';
import { Icon } from '@/ui/components/icon';
import { Button } from '@/ui/components/button';
import { Badge } from '@/ui/components/badge';
import { H1 } from '@/ui/components/heading';
import { UserAvatar } from '@/ui/components/user-avatar';
import { Menu, MenuItem, MenuPopup } from '@/ui/components/menu';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
} from '@/ui/components/alert-dialog';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import { MembersIcon } from '@/ui/components/empty-icons';
import { Table, TableBody, TableRow, TableRowMenuTrigger } from '@/ui/components/table';
import { DataTableHeader, DataTableCell, SortableHeader } from '@/ui/components/data-table';
import { UserInvitationStatus, UserRole } from '@/features/user/constants';
import type { OrganizationUser } from '@/features/organizations/types';
import { organizationUsersQuery } from '@/features/organizations/queries';
import {
  useCancelInvitationToOrg,
  useDeleteOrganizationUser,
  useResendInvitationToOrg,
  useSetOrganizationUserRole,
} from '@/features/organizations/mutations';
import { inviteOrgMemberOpenAtom } from '@/features/organizations/store';
import { InviteOrganizationMember } from '@/features/organizations/components/invite-organization-member';

export const Route = createFileRoute('/_authed/settings/members')({
  component: RouteComponent,
  loader: async ({ context }) => {
    await context.queryClient.query({
      ...organizationUsersQuery(context.auth.user.organizationId),
      staleTime: 'static',
    });
  },
});

function RouteComponent() {
  const auth = useAuth();
  const isAdmin = auth.user.organizationRole === UserRole.ORGANIZATION_ADMIN;
  const [inviteDialogOpen, setInviteDialogOpen] = useAtom(inviteOrgMemberOpenAtom);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const { data } = useSuspenseQuery(organizationUsersQuery(auth.user.organizationId));

  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return data;
    return data.filter(
      (member) =>
        member.name.toLowerCase().includes(query) || member.email.toLowerCase().includes(query),
    );
  }, [data, searchQuery]);

  const columns = [
    {
      accessorKey: 'name',
      size: 200,
      meta: { primary: true },
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <UserAvatar
            user={row.original}
            pending={row.original.invitationStatus === UserInvitationStatus.PENDING}
          />
          {row.original.name}
          {row.original.id === auth.user.id && (
            <span className="text-text-subtle">{t({ en: '(you)', nb: '(du)' })}</span>
          )}
        </div>
      ),
      header: ({ column }) => (
        <SortableHeader column={column} label={t({ en: 'Name', nb: 'Navn' })} />
      ),
    },
    {
      accessorKey: 'email',
      size: 250,
      cell: ({ row }) => <div>{row.original.email}</div>,
      header: ({ column }) => (
        <SortableHeader column={column} label={t({ en: 'Email', nb: 'E-post' })} />
      ),
    },
    {
      accessorKey: 'roleName',
      size: 150,
      cell: ({ row }) => (
        <Badge
          size="sm"
          variant={
            row.original.roleName === UserRole.ORGANIZATION_ADMIN ? 'tertiary-accent' : 'tertiary'
          }
        >
          {row.original.roleName === UserRole.ORGANIZATION_ADMIN
            ? t({ en: 'Admin', nb: 'Admin' })
            : t({ en: 'Member', nb: 'Medlem' })}
        </Badge>
      ),
      header: ({ column }) => (
        <SortableHeader column={column} label={t({ en: 'Role', nb: 'Rolle' })} />
      ),
    },
    {
      accessorKey: 'invitationStatus',
      size: 150,
      cell: ({ row }) => (
        <Badge
          size="sm"
          variant={
            row.original.invitationStatus === UserInvitationStatus.ACCEPTED
              ? 'tertiary-success'
              : row.original.invitationStatus === UserInvitationStatus.PENDING
                ? 'tertiary-warning'
                : 'tertiary-destructive'
          }
        >
          {t({
            en:
              String(row.original.invitationStatus) === '0'
                ? 'Invitation pending'
                : String(row.original.invitationStatus) === '10'
                  ? 'Active'
                  : 'Deactivated',
            nb:
              String(row.original.invitationStatus) === '0'
                ? 'Invitasjon venter'
                : String(row.original.invitationStatus) === '10'
                  ? 'Aktiv'
                  : 'Deaktivert',
          })}
        </Badge>
      ),
      header: ({ column }) => (
        <SortableHeader column={column} label={t({ en: 'Status', nb: 'Status' })} />
      ),
    },
    {
      id: 'actions',
      size: 50,
      enableSorting: false,
      cell: ({ row }) => <MemberRowActions member={row.original} />,
      header: '',
    },
  ] satisfies ColumnDef<OrganizationUser>[];

  const table = useReactTable({
    columns,
    data: filteredData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
    getRowId: (row) => row.id,
  });

  return (
    <>
      <Header>
        <HeaderOrganizationMenu />
        <Breadcrumb>
          <BreadcrumbPage>
            <Icon icon={UserGroupIcon} />
            {t({ en: 'Team', nb: 'Team' })}
          </BreadcrumbPage>
        </Breadcrumb>
        {isAdmin && (
          <HeaderButton
            aria-label={t({ en: 'Invite member', nb: 'Inviter medlem' })}
            onClick={() => setInviteDialogOpen(true)}
          >
            <Icon icon={PlusSignIcon} />
          </HeaderButton>
        )}
        <HeaderCopyButton />
      </Header>
      <MainScrollArea>
        <InviteOrganizationMember open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} />
        <MainHeader className="items-start">
          <div className="flex flex-col items-start gap-2">
            <H1>{t({ en: 'Team', nb: 'Team' })}</H1>
            <p className="text-muted text-sm">
              {t({
                en: 'Everyone in your organisation with access to the platform, with the option to add new members.',
                nb: 'Alle i organisasjonen din med tilgang til plattformen, med mulighet til å legge til nye medlemmer.',
              })}
            </p>
          </div>
          {isAdmin && (
            <Button
              className="max-md:hidden"
              kind="with-icon"
              onClick={() => setInviteDialogOpen(true)}
            >
              <Icon icon={PlusSignIcon} />
              {t({ en: 'Invite member', nb: 'Inviter medlem' })}
            </Button>
          )}
        </MainHeader>
        <MainActions className="border-t!">
          <MainActionsEnd>
            <MainActionsSearchInput
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
            />
          </MainActionsEnd>
        </MainActions>
        {filteredData.length === 0 ? (
          <Empty>
            <EmptyMedia>
              <MembersIcon />
            </EmptyMedia>
            <EmptyContent>
              <EmptyTitle>{t(common.noResults)}</EmptyTitle>
              <EmptyDescription>{t(common.noResultsDescription)}</EmptyDescription>
              <Button variant="secondary" onClick={() => setSearchQuery('')}>
                {t(common.clearFilters)}
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <Table>
            <DataTableHeader table={table} />
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <DataTableCell key={cell.id} cell={cell} />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </MainScrollArea>
    </>
  );
}

function MemberRowActions({ member }: { member: OrganizationUser }) {
  const auth = useAuth();

  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionType, setActionType] = useState<'cancel' | 'delete'>('delete');

  const cancelInvite = useCancelInvitationToOrg();
  const resendInvite = useResendInvitationToOrg();
  const setRole = useSetOrganizationUserRole();
  const deleteMember = useDeleteOrganizationUser();

  const isAdmin = auth.user.organizationRole === UserRole.ORGANIZATION_ADMIN;
  const isActiveAdmin =
    member.roleName === UserRole.ORGANIZATION_ADMIN &&
    member.invitationStatus === UserInvitationStatus.ACCEPTED;

  if (!isAdmin || auth.user.id === member.id || isActiveAdmin) return null;

  const isPending = member.invitationStatus === UserInvitationStatus.PENDING;
  const isAccepted = member.invitationStatus === UserInvitationStatus.ACCEPTED;

  return (
    <div className="text-right">
      <Menu>
        <TableRowMenuTrigger />
        <MenuPopup align="end">
          {!isAccepted ? (
            <>
              <MenuItem
                onClick={() => resendInvite.mutate(member.id)}
                disabled={resendInvite.isPending}
              >
                <Icon icon={MailSend01Icon} />
                {t({ en: 'Resend invitation', nb: 'Send invitasjon på nytt' })}
              </MenuItem>
              {isPending && (
                <MenuItem
                  variant="destructive"
                  onClick={() => {
                    setActionType('cancel');
                    setIsActionOpen(true);
                  }}
                >
                  <Icon icon={CancelIcon} />
                  {t({ en: 'Cancel invitation', nb: 'Avbryt invitasjon' })}
                </MenuItem>
              )}
            </>
          ) : (
            <>
              <MenuItem
                onClick={() =>
                  setRole.mutate({ userId: member.id, roleName: UserRole.ORGANIZATION_ADMIN })
                }
              >
                {t({ en: 'Assign admin role', nb: 'Tildel adminrolle' })}
              </MenuItem>
              <MenuItem
                variant="destructive"
                onClick={() => {
                  setActionType('delete');
                  setIsActionOpen(true);
                }}
              >
                <Icon icon={Delete01Icon} />
                {t({ en: 'Delete member', nb: 'Slett medlem' })}
              </MenuItem>
            </>
          )}
        </MenuPopup>
      </Menu>

      <AlertDialog open={isActionOpen} onOpenChange={setIsActionOpen}>
        <AlertDialogPopup>
          <AlertDialogTitle>
            {actionType === 'cancel'
              ? t({ en: 'Cancel Invitation?', nb: 'Avbryt invitasjon?' })
              : t({ en: 'Delete member?', nb: 'Slett medlem?' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {actionType === 'cancel'
              ? t({
                  en: (
                    <>
                      The invitation email sent to <strong>{member.email}</strong> will be disabled.
                    </>
                  ),
                  nb: (
                    <>
                      Invitasjons-e-posten som er sendt til <strong>{member.email}</strong> vil bli
                      deaktivert.
                    </>
                  ),
                })
              : t({
                  en: (
                    <>
                      Member <strong>{member.email}</strong> will be permanently removed from this
                      organisation. This is irreversible.
                    </>
                  ),
                  nb: (
                    <>
                      Medlem <strong>{member.email}</strong> vil bli permanent fjernet fra denne
                      organisasjonen. Dette er umulig å angre.
                    </>
                  ),
                })}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelInvite.isPending || deleteMember.isPending} />
            <Button
              variant="destructive"
              pending={cancelInvite.isPending || deleteMember.isPending}
              onClick={() => {
                const onSuccess = () => setIsActionOpen(false);
                if (actionType === 'cancel') return cancelInvite.mutate(member.id, { onSuccess });
                deleteMember.mutate(member.id, { onSuccess });
              }}
            >
              {actionType === 'cancel'
                ? t({ en: 'Cancel Invitation', nb: 'Avbryt invitasjon' })
                : t({ en: 'Delete member', nb: 'Slett medlem' })}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </div>
  );
}
